import './CporCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';


function CPORCalculator() {
    const langPrefix = useLanguagePrefix();
    const [operatingCost, setOperatingCost] = useState(null);
    const [occupiedRoom, setOccupiedRoom] = useState(null);
    const [CPOR, setCPOR] = useState(null);
    const { t } = useTranslation();


    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'operating') {
            setOperatingCost(value);
        } else if (type === 'occupied') {
            setOccupiedRoom(value);
        }
    };


    const calculateCPOR = () => {
        if (operatingCost > 0 && occupiedRoom > 0) {
            const result = operatingCost / occupiedRoom;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setCPOR(formatted);
        } else {
            setCPOR(null);
        }
    };

    return (
        <div>
            <Helmet> 

                <title>CPOR Calculator – Cost Per Occupied Room Tool - Prostay</title>
                <meta name="description" content="Use Prostay’s free CPOR calculator to measure your hotel’s cost per occupied room. Understand operational efficiency and control expenses for smarter hotel management." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/cpor-calculator/"} />

                 <link rel="alternate" href="https://www.prostay.com/es/cpor-calculator/" />
                 <link rel="alternate" href="https://www.prostay.com/en/cpor-calculator/" />

                <meta property="og:title" content="CPOR Calculator – Cost Per Occupied Room Tool - Prostay" />
                <meta property="og:description" content="Use Prostay’s free CPOR calculator to measure your hotel’s cost per occupied room. Understand operational efficiency and control expenses for smarter hotel management." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/cpor-calculator/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="CPOR Calculator – Cost Per Occupied Room Tool - Prostay" />
                <meta name="twitter:description" content="Use Prostay’s free CPOR calculator to measure your hotel’s cost per occupied room. Understand operational efficiency and control expenses for smarter hotel management." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />
            </Helmet>
            <div className='d-flex cpor-calculator flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left gap-4'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('CPOR Calculator – Cost Per Occupied Room')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Understand Your Cost Per Occupied Room.')} <br />
                            {t('Use this free CPOR calculator to evaluate your hotel’s operating efficiency and make smarter, cost-conscious business decisions.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Operating Costs')}
                                    </label>
                                    <input
                                        type='number'
                                        value={operatingCost}
                                        onChange={(e) => handleInputChange(e, 'operating')}
                                        placeholder={t('Enter amount here')} />
                                </div>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Occupied Rooms')}
                                    </label>
                                    <input
                                        type='number'
                                        value={occupiedRoom}
                                        onChange={(e) => handleInputChange(e, 'occupied')}
                                        placeholder={t('Enter number here')} />
                                </div>
                                <div className='d-flex gap-3 gap-sm-5 flex-column flex-sm-row align-items-center'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={calculateCPOR}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('CPOR')}
                                        </label>
                                        <input
                                            readOnly
                                            value={CPOR}
                                            type='number'
                                            placeholder='0' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is CPOR?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('CPOR stands for Cost Per Occupied Room, a key metric in hotel operations that measures how much it costs to service each room that is actually sold.')}
                                </p>
                                <br />
                                <p>
                                    {t('Unlike revenue-based metrics like ADR or RevPAR, CPOR focuses entirely on expenses — helping hoteliers evaluate how efficiently they’re managing labor, amenities, housekeeping, and utilities. By calculating CPOR, you can identify unnecessary spending, compare costs between departments or properties, and set realistic financial targets.')}
                                </p>
                                <br />
                                <p>
                                    {t('This metric is especially useful for operational managers, finance teams, and owners looking to control margins without compromising guest experience.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-3'>
                        <h2>
                            {t('CPOR Formula')}
                        </h2>
                        <p>
                            {t('The formula for calculating CPOR is:')}
                        </p>
                        <div className='d-flex align-items-center formula gap-2'>
                            <p>
                                {t('CPOR')}  =
                            </p>
                            <div className='d-flex flex-column align-items-center'>
                                <p style={{ borderBottom: '0.5px solid #1643E0' }}>
                                    {t('Total Operating Costs')}
                                </p>
                                <p>
                                    {t('Occupied Rooms')}
                                </p>
                            </div>
                        </div>
                        <p>
                            {t('To calculate it, divide your total operating costs during a specific period by the number of rooms that were actually occupied during that same period. These operating costs typically include expenses such as housekeeping, front office labor, cleaning supplies, linen services, in-room amenities, and energy costs.')}
                        </p>
                        <p>
                            {t('For example, if your hotel spent $60,000 on operations over one month and sold 1,200 rooms, your CPOR would be $50. That means every occupied room costs you $50 to operate.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3'>
                        <h2>
                            {t('Why CPOR Matters for Hotel Profitability')}
                        </h2>
                        <p>
                            {t('Controlling costs is just as important as increasing revenue, and CPOR is one of the most effective ways to track expense efficiency on a per-room basis. A low CPOR can indicate strong operational control, while a high CPOR may suggest inefficiencies, overspending, or underutilized resources.')}
                        </p>
                        <p>
                            {t('By monitoring CPOR over time, hoteliers can make data-driven decisions about staffing levels, amenity usage, and supplier negotiations. It also helps pinpoint the true cost of guest satisfaction, ensuring that service standards are met without unnecessary budget strain.')}
                        </p>
                        <p>
                            {t('In multi-property groups, CPOR can be used to benchmark performance and highlight which properties are managing their operations most efficiently.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('CPOR vs RevPAR, GOPPAR, and TRevPAR')}
                        </h2>
                        <p>
                            {t('While RevPAR and TRevPAR measure how much revenue your property is generating per room or per bed, CPOR tells you how much you’re spending to service each guest stay.')}
                        </p>
                        <p>
                            {t('GOPPAR subtracts operating costs from revenue to show profit per room, but CPOR focuses purely on the cost side.')}
                        </p>
                        <p>
                            {t('When used together, these metrics allow hotel managers and owners to strike a balance between pricing, sales volume, and operational efficiency. For example, a hotel with strong RevPAR but rising CPOR may still be losing profit. Only by measuring both revenue and cost can you truly optimize performance.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Control Costs More Intelligently with Prostay')}
                        </h2>
                        <p>
                            {t('Prostay makes it easy to track CPOR trends over time, compare across departments or properties, and uncover hidden inefficiencies that may be eating into your margins.')}
                        </p>
                        <p>
                            {t('With integrated dashboards, automated expense tracking, and real-time reporting, Prostay gives hoteliers the power to manage every dollar spent per guest stay. Whether you’re running a lean city hotel or a high-service resort, Prostay helps you reduce costs without sacrificing quality.')}
                        </p>
                    </div>
                </div>
                <ToolFixedMenu/>
            </div>
        </div>
    )
}

export default CPORCalculator;